import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { TextInput } from '@/components/ui/text-input';
import { SocialSignInButtons } from '@/features/auth/social-sign-in-buttons';
import { useRef } from 'react';
import { ActivityIndicator, View, type TextInput as RNTextInput } from 'react-native';
import { AuthLink } from './components/auth-link';
import { AuthScreenLayout } from './components/auth-screen-layout';
import { useRegisterForm } from './hooks/use-register-form';

export function RegisterScreen() {
  const form = useRegisterForm();

  const emailRef = useRef<RNTextInput>(null);
  const usernameRef = useRef<RNTextInput>(null);
  const passwordRef = useRef<RNTextInput>(null);
  const confirmRef = useRef<RNTextInput>(null);
  const inviteRef = useRef<RNTextInput>(null);

  const usernameStatus =
    form.usernameAvailable === true
      ? ('success' as const)
      : form.usernameAvailable === false
        ? ('error' as const)
        : undefined;

  const usernameHint = form.checkingUsername
    ? 'Checking availability...'
    : form.usernameAvailable === true
      ? 'Username is available!'
      : form.usernameAvailable === false
        ? 'Username is already taken'
        : '3-30 characters, letters and numbers only';

  return (
    <AuthScreenLayout
      title="Sign Up"
      heading="Sign up"
      subheading="Dish-level opinions from people you trust"
      footer={
        <View className="flex-row justify-center items-center gap-1">
          <Text className="text-sm text-warm-gray-300">Already have an account?</Text>
          <AuthLink onPress={form.navigateToLogin} testID="register-login-link">
            Log in
          </AuthLink>
        </View>
      }
      socialSlot={
        <View className="gap-2">
          <SocialSignInButtons
            context="register"
            inviteCode={form.inviteCode}
            disabled={!form.inviteValid}
          />
          {!form.inviteValid && (
            <Text className="text-xs text-center text-warm-gray-300">
              Enter your invite code below to continue with Google or Apple.
            </Text>
          )}
        </View>
      }
    >
      <View className="gap-4">
        <TextInput
          placeholder="Full Name"
          value={form.name}
          onChangeText={(text) => {
            form.setName(text);
            form.clearFieldError('name');
          }}
          autoComplete="name"
          textContentType="name"
          returnKeyType="next"
          onSubmitEditing={() => emailRef.current?.focus()}
          submitBehavior="submit"
          error={form.errors.name || undefined}
        />

        <TextInput
          ref={emailRef}
          placeholder="Email"
          value={form.email}
          onChangeText={(text) => {
            form.setEmail(text);
            form.clearFieldError('email');
          }}
          autoCapitalize="none"
          autoComplete="email"
          textContentType="emailAddress"
          keyboardType="email-address"
          returnKeyType="next"
          onSubmitEditing={() => usernameRef.current?.focus()}
          submitBehavior="submit"
          error={form.errors.email || undefined}
        />

        <TextInput
          ref={usernameRef}
          placeholder="Username"
          value={form.username}
          onChangeText={(text) => {
            form.handleUsernameChange(text);
            form.clearFieldError('username');
          }}
          autoCapitalize="none"
          autoCorrect={false}
          autoComplete="username"
          textContentType="username"
          returnKeyType="next"
          onSubmitEditing={() => passwordRef.current?.focus()}
          submitBehavior="submit"
          error={form.errors.username || undefined}
          status={usernameStatus}
          helperText={usernameHint}
          leftIcon={<Text className="text-muted-foreground text-base">@</Text>}
          rightIcon={
            form.checkingUsername ? (
              <ActivityIndicator size="small" />
            ) : form.usernameAvailable === true ? (
              <Text className="text-success text-lg">✓</Text>
            ) : form.usernameAvailable === false ? (
              <Text className="text-error text-lg">✕</Text>
            ) : undefined
          }
        />

        <TextInput
          ref={passwordRef}
          placeholder="Password"
          value={form.password}
          onChangeText={(text) => {
            form.setPassword(text);
            form.clearFieldError('password');
          }}
          secureTextEntry
          autoCapitalize="none"
          autoCorrect={false}
          spellCheck={false}
          autoComplete="new-password"
          textContentType="newPassword"
          returnKeyType="next"
          onSubmitEditing={() => confirmRef.current?.focus()}
          submitBehavior="submit"
          error={form.errors.password || undefined}
        />

        <TextInput
          ref={confirmRef}
          placeholder="Confirm Password"
          value={form.confirmPassword}
          onChangeText={(text) => {
            form.setConfirmPassword(text);
            form.clearFieldError('confirmPassword');
          }}
          secureTextEntry
          autoCapitalize="none"
          autoCorrect={false}
          spellCheck={false}
          autoComplete="new-password"
          textContentType="newPassword"
          returnKeyType="next"
          onSubmitEditing={() => inviteRef.current?.focus()}
          submitBehavior="submit"
          error={form.errors.confirmPassword || undefined}
        />

        <TextInput
          ref={inviteRef}
          placeholder="XXXXX-XXXXX"
          // The accessible name falls back to the placeholder, which would be read
          // out as a run of X's.
          accessibilityLabel="Invite code"
          value={form.inviteCode}
          onChangeText={(text) => {
            form.handleInviteCodeChange(text);
            form.clearFieldError('inviteCode');
          }}
          autoCapitalize="characters"
          autoCorrect={false}
          autoComplete="off"
          textContentType="none"
          returnKeyType="go"
          onSubmitEditing={form.handleRegister}
          error={form.errors.inviteCode || undefined}
          helperText="Enter your 10-character invite code"
          inputStyle={{ fontFamily: 'monospace', letterSpacing: 2 }}
        />

        <Button
          size="lg"
          fullWidth
          className="mt-2"
          onPress={form.handleRegister}
          loading={form.isLoading}
          testID="register-submit"
        >
          Create your account
        </Button>
      </View>
    </AuthScreenLayout>
  );
}
